import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Define helper function first
  const applyThemeImmediately = (theme) => {
    const colors = {
      black: '#000000',
      grey: '#1a1a1a',
      white: '#ffffff'
    };
    const bgColor = colors[theme] || colors.black;
    const textColor = theme === 'white' ? '#000000' : '#ffffff';
    
    // Apply immediately to all elements
    document.body.style.setProperty('background-color', bgColor, 'important');
    document.documentElement.style.setProperty('background-color', bgColor, 'important');
    document.body.style.setProperty('color', textColor, 'important');
    document.documentElement.style.setProperty('color', textColor, 'important');
    
    const root = document.getElementById('root');
    if (root) {
      root.style.setProperty('background-color', bgColor, 'important');
      root.style.setProperty('color', textColor, 'important');
    }
    
    // Set data attribute for CSS targeting
    document.body.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    
    console.log('🎨 Theme applied globally:', {
      theme,
      bgColor,
      textColor,
      timestamp: new Date().toISOString()
    });
  };

  // Get initial theme from localStorage or default to 'black'
  const [backgroundColor, setBackgroundColor] = useState(() => {
    const savedTheme = localStorage.getItem('backgroundColor');
    // Apply theme immediately on mount
    const theme = savedTheme || 'black';
    applyThemeImmediately(theme);
    return theme;
  });

  // Save theme to localStorage and apply styles whenever it changes
  useEffect(() => {
    localStorage.setItem('backgroundColor', backgroundColor);
    applyThemeImmediately(backgroundColor);
  }, [backgroundColor]);

  const getBackgroundColor = (theme) => {
    const colors = {
      black: '#000000',
      grey: '#1a1a1a',
      white: '#ffffff'
    };
    return colors[theme] || colors.black;
  };

  const changeBackgroundColor = (color) => {
    console.log('📝 Theme change requested:', color);
    if (!color) {
      console.warn('❌ Invalid color: color is null or undefined');
      return false;
    }
    if (['black', 'grey', 'white'].includes(color)) {
      setBackgroundColor(color);
      console.log('✅ Theme change accepted:', color);
      return true;
    } else {
      console.warn('❌ Invalid color:', color, 'Valid options are: black, grey, white');
      return false;
    }
  };

  const value = {
    backgroundColor,
    changeBackgroundColor,
    getBackgroundColor
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
